import { useState, useEffect, useCallback } from 'react';
import { Play, Square, Star, Settings, Wand2, Loader2, History } from 'lucide-react';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useBackend } from '@/services/backend';
import { useUIStore } from '@/stores/uiStore';
import { logger } from '@/lib/logger';
import {
  getProjectRunnerSettings,
  setProjectRunnerSettings,
  addToRunnerHistory,
  type RunConfiguration,
  type AteliercodeRunners,
} from '@/lib/projectSettings';
import { detectRunConfigurations, type DetectionContext } from '@/lib/runConfigDetector';

interface RunnersPanelProps {
  projectPath?: string;
}

/**
 * Runners as a first-class dockable panel — the same run configurations that
 * live in the terminal's Run button, surfaced as a movable panel so it can be
 * docked to any edge / tabset. Launching a config still routes into the
 * terminal (setPendingCommand + requestTabSwitch), exactly like RunButton.
 */
export default function RunnersPanel({ projectPath }: RunnersPanelProps) {
  const backend = useBackend();
  const project = useProjectStore((s) => s.currentProject);
  const updateProjectInStore = useProjectStore((s) => s.updateProject);
  const openSettings = useUIStore((s) => s.openSettings);

  const safePath = projectPath ?? project?.root_path ?? '__default__';

  // File-based configs from .ateliercode/runners.json (primary source)
  const [fileConfigs, setFileConfigs] = useState<RunConfiguration[] | null>(null);
  const [fileDefault, setFileDefault] = useState<string | null>(null);
  const [detectedScripts, setDetectedScripts] = useState<RunConfiguration[] | null>(null);
  const [isAutoConfiguring, setIsAutoConfiguring] = useState(false);

  // Fallback: legacy settings-blob configs + history
  const runnerSettings = getProjectRunnerSettings(project?.settings ?? null);
  const configs = fileConfigs ?? runnerSettings.configs;
  const defaultConfigId = fileDefault
    ? configs.find((c) => c.name === fileDefault)?.id ?? configs[0]?.id ?? null
    : runnerSettings.defaultConfigId;
  const { history } = runnerSettings;

  // Run state for this project's run terminal
  const runSessionId = useTerminalStore((s) => s.runSessionIds.get(safePath));
  const runSession = useTerminalStore((s) =>
    runSessionId != null ? s.sessions.get(runSessionId) : undefined,
  );
  const isRunning = runSession != null && runSession.status === 'active';

  // Load configs from .ateliercode/runners.json on mount + on window focus
  // (mirrors RunButton so external edits to runners.json are picked up).
  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    const projectId = project.id;

    const reread = async () => {
      try {
        const raw = await backend.files.readAteliercodeRunners(projectId);
        if (cancelled || !raw) return;
        const parsed: AteliercodeRunners = JSON.parse(raw);
        if (parsed.configs && Array.isArray(parsed.configs)) {
          let counter = 0;
          setFileConfigs(
            parsed.configs.map((c) => ({
              id: Date.now().toString(36) + (++counter).toString(36),
              name: c.name,
              command: c.command,
              shell: c.shell,
            })),
          );
          setFileDefault(parsed.default ?? null);
        }
      } catch (e) {
        logger.warn('RunnersPanel', 'Failed to load .ateliercode/runners.json', e);
      }
    };

    void reread();
    const onFocus = () => { void reread(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [project?.id, backend]);

  // Reset ephemeral state on project switch
  useEffect(() => {
    setFileConfigs(null);
    setFileDefault(null);
    setDetectedScripts(null);
  }, [project?.id]);

  // Auto-detect scripts when there are no configs
  useEffect(() => {
    if (configs.length > 0 || !project?.id || !project?.root_path) return;
    if (detectedScripts !== null) return;
    const readFile = (path: string) => backend.files.readFile(project.id, path);
    backend.files
      .getFolderChildren(project.id, project.root_path)
      .then((nodes) => {
        const ctx: DetectionContext = { readFile, rootFiles: nodes.map((n) => n.name) };
        return detectRunConfigurations(ctx);
      })
      .then((results) => setDetectedScripts(results.length > 0 ? results : []))
      .catch(() => setDetectedScripts([]));
  }, [configs.length, project?.id, project?.root_path, backend, detectedScripts]);

  const runConfig = useCallback(
    async (config: RunConfiguration) => {
      const store = useTerminalStore.getState();
      store.setPendingCommand({
        command: config.command,
        title: config.name,
        projectPath: safePath,
        shell: config.shell && config.shell !== 'default' ? config.shell : undefined,
        kind: 'run',
        runConfig: config.name,
      });
      store.requestTabSwitch();
      if (project?.id) {
        try {
          const updatedSettings = addToRunnerHistory(project.settings, {
            name: config.name,
            command: config.command,
          });
          await backend.projects.update(project.id, { settings: updatedSettings });
          await updateProjectInStore(project.id, { settings: updatedSettings });
        } catch {
          // Non-critical
        }
      }
    },
    [safePath, project, backend, updateProjectInStore],
  );

  const stopRun = useCallback(() => {
    if (runSessionId != null) {
      backend.terminal.close(runSessionId).catch((e) => logger.warn('RunnersPanel', 'close failed', e));
    }
  }, [runSessionId, backend]);

  const handleAutoConfigure = useCallback(async () => {
    if (!project?.id) return;
    setIsAutoConfiguring(true);
    try {
      const readFile = (path: string) => backend.files.readFile(project.id, path);
      const rootNodes = await backend.files.getFolderChildren(project.id, project.root_path);
      const ctx: DetectionContext = { readFile, rootFiles: rootNodes.map((n) => n.name) };
      const detected = await detectRunConfigurations(ctx);
      if (detected.length === 0) return;

      const existingCommands = new Set(configs.map((c) => c.command));
      const newConfigs = detected.filter((c) => !existingCommands.has(c.command));
      const merged = [...configs, ...newConfigs];
      const newDefault = fileDefault ?? merged[0]?.name ?? null;

      const runners: AteliercodeRunners = {
        $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
        configs: merged.map((c) => ({
          name: c.name,
          command: c.command,
          ...(c.shell && c.shell !== 'default' ? { shell: c.shell } : {}),
        })),
        default: newDefault,
      };
      await backend.files.writeAteliercodeRunners(project.id, JSON.stringify(runners, null, 2));

      let counter = 0;
      setFileConfigs(merged.map((c) => ({ ...c, id: Date.now().toString(36) + (++counter).toString(36) })));
      setFileDefault(newDefault);

      const updatedSettings = setProjectRunnerSettings(project.settings, {
        configs: merged,
        defaultConfigId: merged[0]?.id ?? null,
      });
      await backend.projects.update(project.id, { settings: updatedSettings });
      await updateProjectInStore(project.id, { settings: updatedSettings });
      setDetectedScripts(null);
    } catch {
      // silent — user can retry
    } finally {
      setIsAutoConfiguring(false);
    }
  }, [project, backend, configs, fileDefault, updateProjectInStore]);

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-base-100 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-base-300 sticky top-0 bg-base-100 z-10">
        <span className="text-[11px] uppercase tracking-wider text-base-content/60 font-semibold">
          Runners
        </span>
        {isRunning && (
          <button
            onClick={stopRun}
            className="flex items-center gap-1 text-xs text-error hover:bg-error/10 px-1.5 py-0.5 rounded"
            title="Stop the running configuration"
          >
            <Square className="w-3.5 h-3.5" /> Stop
          </button>
        )}
      </div>

      {/* Configurations */}
      {configs.length > 0 ? (
        <div className="py-1">
          {configs.map((config) => (
            <button
              key={config.id}
              onClick={() => runConfig(config)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-base-200 cursor-pointer transition-colors text-left"
              title={config.command}
            >
              <Play className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="truncate flex-1">{config.name}</span>
              {config.id === defaultConfigId && (
                <Star className="w-3 h-3 text-amber-500 shrink-0 fill-amber-500" />
              )}
            </button>
          ))}
        </div>
      ) : detectedScripts && detectedScripts.length > 0 ? (
        <div className="py-1">
          <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-base-content/40">
            Detected Scripts
          </div>
          {detectedScripts.map((script) => (
            <button
              key={script.id}
              onClick={() => runConfig(script)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-base-200 cursor-pointer transition-colors text-left"
              title={script.command}
            >
              <Play className="w-3.5 h-3.5 text-emerald-500/60 shrink-0" />
              <span className="truncate">{script.name}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="px-3 py-3 text-xs text-base-content/50">No run configurations yet.</div>
      )}

      {/* Recent history */}
      {history.length > 0 && (
        <div className="py-1 border-t border-base-300">
          <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-base-content/40">Recent</div>
          {history.slice(0, 5).map((entry, i) => {
            if (configs.some((c) => c.command === entry.command)) return null;
            return (
              <button
                key={`history-${i}`}
                onClick={() => runConfig({ id: `history-${i}`, name: entry.name, command: entry.command })}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-base-200 cursor-pointer transition-colors text-left"
                title={entry.command}
              >
                <History className="w-3.5 h-3.5 text-base-content/50 shrink-0" />
                <span className="truncate flex-1">{entry.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto border-t border-base-300 py-1">
        <button
          onClick={handleAutoConfigure}
          disabled={isAutoConfiguring}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content/70 hover:bg-base-200 cursor-pointer transition-colors text-left disabled:opacity-50"
        >
          {isAutoConfiguring ? (
            <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" />
          ) : (
            <Wand2 className="w-3.5 h-3.5 shrink-0" />
          )}
          <span>{isAutoConfiguring ? 'Detecting…' : 'Auto Configure'}</span>
        </button>
        <button
          onClick={() => openSettings()}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content/70 hover:bg-base-200 cursor-pointer transition-colors text-left"
        >
          <Settings className="w-3.5 h-3.5 shrink-0" />
          <span>Edit Configurations…</span>
        </button>
      </div>
    </div>
  );
}
